/**
 * M3X.WORKSPACE F.5 — `useWorkspace()` hook.
 *
 * Per spec §2.3 — single navigation-context hook every page consumes.
 * Centralizes:
 *   - currentModule / currentSubDomain / currentScreen
 *   - rail2State / setRail2State / cycleRail2
 *   - pinnedSubDomains (F.12 — user customisable)
 *
 * Per-user persistence via localStorage. User id source = the optional
 * `userId` arg (caller passes Clerk's session id). Anonymous → "anon".
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import {
  NAVIGATION_REGISTRY,
  type ModuleId,
  type Screen,
  type SubDomain,
  screenForRoute,
} from "../navigation/registry";
import { moduleForPath } from "./Rail1";
import type { Rail2State } from "./Rail2";
import { useRail2State } from "./useRail2State";


// ---------------------------------------------------------------------------
// Pinned sub-domains (F.12 — user customisable)
// ---------------------------------------------------------------------------


/**
 * PR 5 — locked storage key per closure-prompt §5.
 *   solvait.workspace.pinnedSubDomains.{clerkUserId}
 *
 * `_LEGACY_PIN_KEY` (workspace:pinned-sub-domains:{userId}) is read
 * once on init so an existing user's pre-S2 pins migrate forward.
 */
function _pinnedKey(userId: string | null): string {
  return `solvait.workspace.pinnedSubDomains.${userId ?? "anon"}`;
}


function _legacyPinnedKey(userId: string | null): string {
  return `workspace:pinned-sub-domains:${userId ?? "anon"}`;
}


/** PR 5 — max pins per user (closure-prompt §5). */
export const MAX_PINS = 5;


/**
 * M3X.WORKSPACE-CLOSURE Session 2.6 PR 2.A — default pins seeded on
 * first login so the PINNED section is visibly populated without
 * requiring the user to manually pin anything.
 *
 * Sub-domain ids match the registry (NAVIGATION_REGISTRY.id):
 *   - operations-cross-cutting  — hosts the HR Operations Hub +
 *                                 Approvals Queue + Import Hub
 *   - operations-leaves         — the most-used HR workflow
 *   - people-watch-lists        — compliance alerts surface
 *
 * Seeded ONLY when localStorage returns null (never set). If the
 * user pinned then unpinned everything ('[]'), we respect that and
 * don't re-seed.
 */
export const DEFAULT_PINNED_SUB_DOMAINS: readonly string[] = [
  "operations-cross-cutting",
  "operations-leaves",
  "people-watch-lists",
];


function _readPinned(userId: string | null): string[] {
  if (typeof window === "undefined") return [...DEFAULT_PINNED_SUB_DOMAINS];
  try {
    let raw = window.localStorage.getItem(_pinnedKey(userId));
    let legacyHit = false;
    if (!raw) {
      // One-time migration from the legacy key. Don't delete the
      // legacy key — Rule 4 prohibits destructive cleanup.
      raw = window.localStorage.getItem(_legacyPinnedKey(userId));
      if (raw) legacyHit = true;
    }
    // First-login seed (closure-hotfix S2.6 §2.A): null means "never
    // set" → seed defaults. An empty array string '[]' means "user
    // explicitly unpinned everything" → respect, don't re-seed.
    if (!raw && !legacyHit) {
      // Write the seed so this only happens once. Future writes
      // (via pin/unpin) overwrite this with the user's choices.
      const seeded = [...DEFAULT_PINNED_SUB_DOMAINS];
      _writePinned(userId, seeded);
      return seeded;
    }
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((s) => typeof s === "string") : [];
  } catch {
    return [];
  }
}


function _writePinned(userId: string | null, pinned: string[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(_pinnedKey(userId), JSON.stringify(pinned));
  } catch {
    // ignore
  }
}


/** PR 5 — error surfaced when the user pins past MAX_PINS. */
export type PinError = "limit_reached" | null;


// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------


export interface UseWorkspaceResult {
  currentModule: ModuleId | null;
  currentSubDomain: SubDomain | null;
  currentScreen: Screen | null;
  /** User's preferred Rail 2 state (unchanged by force-hide). */
  rail2State: Rail2State;
  /**
   * Effective Rail 2 state used by the shell to render width. When
   * the active module has zero sub-domains (Mission Control), this
   * is forced to "hidden" so an empty 256px column doesn't render —
   * the user's preference is preserved unchanged.
   */
  effectiveRail2State: Rail2State;
  setRail2State: (next: Rail2State) => void;
  cycleRail2: () => void;
  pinnedSubDomains: string[];
  pinSubDomain: (id: string) => void;
  unpinSubDomain: (id: string) => void;
  isPinned: (id: string) => boolean;
  /** PR 5 — transient pin error. Cleared after 5s. */
  pinError: PinError;
  /** PR 5 — explicit clear (used by the toast's manual dismiss). */
  clearPinError: () => void;
}


export function useWorkspace(
  userId: string | null = null,
): UseWorkspaceResult {
  const location = useLocation();
  const { rail2State, setRail2State, cycleRail2 } = useRail2State(userId);

  const screenMatch = useMemo(
    () => screenForRoute(location.pathname),
    [location.pathname],
  );

  // Prefer the registry's module (hyphen-case, matches the ModuleId
  // union). Fall back to Rail 1's pathPrefix map (the moduleForPath
  // ids use underscores; normalize). Mission Control's index route
  // /mission-control matches the registry, so this is the canonical
  // path.
  const currentModule = useMemo<ModuleId | null>(() => {
    if (screenMatch) return screenMatch.subDomain.module;
    const rail1Id = moduleForPath(location.pathname)?.id;
    if (!rail1Id) return null;
    // Normalize: rail1 uses "mission_control"; registry uses "mission-control"
    return rail1Id.replace(/_/g, "-") as ModuleId;
  }, [location.pathname, screenMatch]);

  const [pinned, setPinned] = useState<string[]>(() => _readPinned(userId));
  const [pinError, setPinError] = useState<PinError>(null);

  useEffect(() => {
    setPinned(_readPinned(userId));
  }, [userId]);

  // PR 5 — auto-clear the pin error after 5 seconds
  useEffect(() => {
    if (pinError === null) return;
    const t = setTimeout(() => setPinError(null), 5_000);
    return () => clearTimeout(t);
  }, [pinError]);

  const pinSubDomain = useCallback(
    (id: string) => {
      setPinned((cur) => {
        if (cur.includes(id)) return cur;
        // PR 5 — 5-pin limit
        if (cur.length >= MAX_PINS) {
          setPinError("limit_reached");
          return cur;
        }
        // PR 5 — most-recently-pinned at the TOP (closure-prompt §5)
        const next = [id, ...cur];
        _writePinned(userId, next);
        return next;
      });
    },
    [userId],
  );

  const unpinSubDomain = useCallback(
    (id: string) => {
      setPinned((cur) => {
        const next = cur.filter((p) => p !== id);
        _writePinned(userId, next);
        return next;
      });
    },
    [userId],
  );

  const isPinned = useCallback((id: string) => pinned.includes(id), [pinned]);

  // M3X.WORKSPACE-CLOSURE Session 2 PR 2 — force Rail 2 hidden when
  // the active module is single-page (Mission Control). The spec
  // calls Mission Control a "no sub-domains" module — in the
  // registry it has one self sub-domain with one landing screen,
  // so navigating to /mission-control would render an empty 256px
  // Rail 2 column. Detection: total screen count across all
  // sub-domains for that module ≤ 1. The user's preference
  // (rail2State) is preserved; only the EFFECTIVE state changes.
  const effectiveRail2State = useMemo<Rail2State>(() => {
    if (currentModule === null) return rail2State;
    const screenCount = NAVIGATION_REGISTRY.filter(
      (sd) => sd.module === currentModule,
    ).reduce((acc, sd) => acc + sd.screens.length, 0);
    if (screenCount <= 1) return "hidden";
    return rail2State;
  }, [currentModule, rail2State]);

  const clearPinError = useCallback(() => setPinError(null), []);

  return {
    currentModule,
    currentSubDomain: screenMatch?.subDomain ?? null,
    currentScreen: screenMatch?.screen ?? null,
    rail2State,
    effectiveRail2State,
    setRail2State,
    cycleRail2,
    pinnedSubDomains: pinned,
    pinSubDomain,
    unpinSubDomain,
    isPinned,
    pinError,
    clearPinError,
  };
}
